import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';
import BatchImageResizer from '../components/BatchImageResizer';

const { 
  FiLayers, FiZap, FiShield, FiDownload, FiSettings, FiImage, 
  FiStar, FiUsers, FiTrendingUp, FiClock, FiCheckCircle 
} = FiIcons;

function BatchResize() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title="Free Batch Image Resizer - Resize Multiple Images Online"
        description="Batch resize hundreds of images at once for free! Professional bulk image resizing with custom dimensions, aspect ratio preservation, and secure processing. Perfect for websites, e-commerce, and photo libraries."
        keywords="batch image resizer, bulk image resize, resize multiple images, bulk photo resizer, batch image processing, multiple image resizer, bulk JPEG resizer, batch PNG resize, website image resize, bulk photo processor"
        url="/batch-resize"
        image="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=630&fit=crop"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              <div className="bg-blue-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiLayers} className="text-white text-3xl" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Free Batch Image Resizer
                <span className="block text-blue-600">Resize Multiple Images</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Resize hundreds of images at once with our professional batch processing tool. 
                Set custom dimensions, maintain aspect ratios - completely free and secure.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Batch Resizer Tool */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <BatchImageResizer />
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-4">
                Ready to Resize Your Images?
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Start batch resizing your images now - it's fast, free, and secure!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors"
                >
                  Start Batch Resizing
                </motion.button>
                <Link to="/">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-800 transition-colors"
                  >
                    Try Single Image Resizer
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}

export default BatchResize;